import { DeleteIcon, MoreIcon, RenameIcon } from "@/components/icons";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";

import { errorMessage } from "../lib/query.ts";

import {
  canSaveTitle,
  namedTitle,
  TITLE_MAX_LENGTH,
  UNTITLED_CONVERSATION,
  useDeleteConversation,
  useRenameConversation,
  type ConversationSummary,
} from "../chat/conversations.ts";
import { useSession } from "./session.tsx";
import { CONVERSATION_TEXTS } from "./texts.ts";

interface Props {
  readonly conversation: ConversationSummary;
  /** Se acaba de borrar esta conversación. Quien lee decide qué hacer. */
  onDeleted: (conversationId: string) => void;
}

type Action = "rename" | "delete";

/**
 * Renombrar y borrar una conversación, desde su fila del historial.
 *
 * Van detrás de un menú y no como dos botones a la vista porque la fila es
 * para abrir la conversación: lo que se hace veinte veces al día tiene que
 * seguir siendo un clic en cualquier punto de la fila, y lo que se hace una
 * vez al mes puede costar dos.
 *
 * El diálogo se MONTA al elegir la acción, en vez de estar siempre puesto y
 * oculto. Así el campo del renombrado nace cada vez con el título de ahora, y
 * no con lo que alguien dejó escrito y canceló hace un rato.
 */
export function ConversationActions({ conversation, onDeleted }: Props) {
  const [action, setAction] = useState<Action | null>(null);
  const name = namedTitle(conversation.title) ?? UNTITLED_CONVERSATION;

  const close = () => {
    setAction(null);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* Invisible hasta que el ratón pasa por la fila o el teclado llega
              a ella, pero SIEMPRE en el árbol: un botón que se monta al
              pasar el ratón no existe para quien navega con el tabulador. */}
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={CONVERSATION_TEXTS.actionsOf(name)}
            className="mr-1 opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
          >
            <MoreIcon />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => {
              setAction("rename");
            }}
          >
            <RenameIcon /> {CONVERSATION_TEXTS.rename}
          </DropdownMenuItem>
          <DropdownMenuItem
            variant="destructive"
            onSelect={() => {
              setAction("delete");
            }}
          >
            <DeleteIcon /> {CONVERSATION_TEXTS.delete}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {action === "rename" && (
        <RenameDialog conversation={conversation} onClose={close} />
      )}

      {action === "delete" && (
        <DeleteDialog
          conversation={conversation}
          onClose={close}
          onDeleted={onDeleted}
        />
      )}
    </>
  );
}

/**
 * El diálogo de renombrar.
 *
 * Se queda abierto mientras el servidor contesta y solo se cierra si acepta:
 * si falla, el sitio donde estaba el nombre escrito es el sitio donde hay que
 * enseñar el fallo, con lo tecleado todavía ahí.
 */
function RenameDialog({
  conversation,
  onClose,
}: {
  readonly conversation: ConversationSummary;
  onClose: () => void;
}) {
  const current = namedTitle(conversation.title);
  const [draft, setDraft] = useState(current ?? "");
  const userId = useSession().me?.userId ?? "";
  const rename = useRenameConversation(userId);

  const save = () => {
    if (!canSaveTitle(draft, current) || rename.isPending) return;
    rename.mutate(
      { id: conversation.id, title: draft.trim() },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            save();
          }}
        >
          <DialogHeader>
            <DialogTitle>{CONVERSATION_TEXTS.renameTitle}</DialogTitle>
            <DialogDescription>
              {CONVERSATION_TEXTS.renameHint}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-1.5">
            <Label htmlFor="conversation-title">
              {CONVERSATION_TEXTS.name}
            </Label>
            <Input
              id="conversation-title"
              value={draft}
              maxLength={TITLE_MAX_LENGTH}
              autoFocus
              placeholder={UNTITLED_CONVERSATION}
              onChange={(event) => {
                setDraft(event.target.value);
              }}
            />
          </div>

          {rename.isError && (
            <Alert variant="destructive">
              <AlertDescription>{errorMessage(rename.error)}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {CONVERSATION_TEXTS.cancel}
            </Button>
            <Button
              type="submit"
              disabled={!canSaveTitle(draft, current) || rename.isPending}
            >
              {rename.isPending
                ? CONVERSATION_TEXTS.saving
                : CONVERSATION_TEXTS.save}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * El diálogo de borrar.
 *
 * Dice las dos cosas que nadie se espera --que no hay vuelta atrás y que se
 * van también los archivos adjuntos-- y NOMBRA la conversación, que es lo que
 * evita borrar la de al lado.
 */
function DeleteDialog({
  conversation,
  onClose,
  onDeleted,
}: {
  readonly conversation: ConversationSummary;
  /** Cancelar. Cerrar al terminar no hace falta: ver abajo. */
  onClose: () => void;
  onDeleted: (conversationId: string) => void;
}) {
  const name = namedTitle(conversation.title) ?? UNTITLED_CONVERSATION;
  // Nadie cierra este diálogo al acabar: la fila desaparece de la lista y se
  // lo lleva consigo. Solo sigue en pantalla si el borrado falla, que es justo
  // cuando hace falta que siga.
  const userId = useSession().me?.userId ?? "";
  const remove = useDeleteConversation(userId, onDeleted);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{CONVERSATION_TEXTS.deleteTitle(name)}</DialogTitle>
          <DialogDescription>{CONVERSATION_TEXTS.deleteHint}</DialogDescription>
        </DialogHeader>

        {remove.isError && (
          <Alert variant="destructive">
            <AlertDescription>{errorMessage(remove.error)}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {CONVERSATION_TEXTS.cancel}
          </Button>
          <Button
            variant="destructive"
            disabled={remove.isPending}
            onClick={() => {
              remove.mutate(conversation.id);
            }}
          >
            {remove.isPending
              ? CONVERSATION_TEXTS.deleting
              : CONVERSATION_TEXTS.delete}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
